import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	IDrawerDataListColumn,
	Id,
	Loading,
	PaginatedDrawerAutoDataList,
	PaginatedListFetcherConfig,
	isSuccess,
	laravelPaginatedListFetchConfig,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useContactListUtils } from "../../../contacts/useContactListUtils";
import { LocalContactViewSwitcher } from "../../../contactViews/local/LocalContactViewSwitcher";
import { createInvitationListService } from "../../../shared/api/invitationListService";
import { useContactView } from "../../../shared/hooks/useContactView";
import {
	IInvitationListAttachMultiContactsFormState,
	IInvitationListAttachSingleContactRequest,
	IInvitationListIndexResource,
} from "../../../shared/types/invitationList";
import { useAttendanceTranslations } from "./AttendanceSelectField";
import { InvitationListAttachMultiContactsDrawer } from "./InvitationListAttachMultiContactsDrawer";
import { InvitationListUpdateDrawer } from "./InvitationListUpdateDrawer";

export const InvitationList: FC = () => {
	const { t } = useTranslation();
	const translationMap = useAttendanceTranslations();
	const { eventId = "" } = useParams<"eventId">();
	const service = useMemo(() => createInvitationListService(eventId), [eventId]);
	const queryClient = useQueryClient();
	const { showToast } = useToasts();
	const submitAttachMultiContacts = useCallback(
		async ({ distributionList, contacts, ...values }: IInvitationListAttachMultiContactsFormState) => {
			const res = await service.attachContacts({
				...values,
				distributionListId: distributionList?.id ?? null,
				contactIds: contacts.map((x) => x.id),
			});

			if (isSuccess(res)) {
				showToast({
					type: "success",
					title: t("Invitation list"),
					body: t("Invitation list was successfully updated."),
				});
				await queryClient.invalidateQueries({ queryKey: service.indexKey });

				return true;
			}
			return false;
		},
		[queryClient, service, showToast, t],
	);
	const upsertInvitationListContact = useCallback(
		async (contactId: Id, entityRequest: IInvitationListAttachSingleContactRequest) => {
			const response = await service.update(contactId, entityRequest);

			if (isSuccess(response)) {
				showToast({
					body: t("Invitation list was successfully updated."),
					title: t("Invitation list"),
					type: "success",
				});

				await queryClient.invalidateQueries({ queryKey: service.indexKey });

				return true;
			}
			return false;
		},
		[queryClient, service, showToast, t],
	);
	const removeContactFromInvitationList = useCallback(
		async (id: Id) => {
			const response = await service.destroy(id);

			if (isSuccess(response)) {
				showToast({
					body: t("Invitation list was successfully updated."),
					title: t("Invitation list"),
					type: "success",
				});
				await queryClient.invalidateQueries({ queryKey: service.indexKey });

				return true;
			}
			return false;
		},
		[queryClient, service, showToast, t],
	);

	const { currentContactView, isLoading: contactViewLoading } = useContactView();
	const { columns } = useContactListUtils(currentContactView);
	const dataListColumns: IDrawerDataListColumn<IInvitationListIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Attendance prediction"),
				valueKey: "attendancePrediction",
				render: ({ attendancePrediction }) => translationMap[attendancePrediction],
				type: "value",
				sortable: true,
			},
			{
				heading: t("Person count"),
				valueKey: "personCount",
				type: "value",
				sortable: true,
			},
			...(columns as unknown as IDrawerDataListColumn<IInvitationListIndexResource>[]),
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("Update invitation list entry")}
							label={t("Update invitation list entry")}
							action={openDrawer}
							icon="edit"
							shortcut="primary"
						/>
						<ContextMenuDeleteItem
							action={() => removeContactFromInvitationList(id)}
							title={t("Remove contact from invitation list")}
							label={t("Remove contact from invitation list")}
							heading={t("Remove")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
						>
							{t("Are you sure you want to remove this contact from the invitation list?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[columns, removeContactFromInvitationList, t, translationMap],
	);

	if (contactViewLoading) {
		return <Loading />;
	}

	return (
		<PaginatedDrawerAutoDataList
			canCreate
			canUpdate
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(
				[...service.indexKey, `${currentContactView?.id ?? null}`],
				(config: PaginatedListFetcherConfig<IInvitationListIndexResource>) =>
					service.index(config, currentContactView?.id ?? null),
			)}
			headerActions={(createButton) => (
				<>
					<LocalContactViewSwitcher />
					{createButton}
				</>
			)}
			renderCreateDrawer={(drawerProps) => (
				<InvitationListAttachMultiContactsDrawer
					{...drawerProps}
					onSubmit={(values) => submitAttachMultiContacts(values)}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<InvitationListUpdateDrawer
					{...drawerProps}
					service={service}
					onSubmit={(values) => upsertInvitationListContact(drawerProps.id, values)}
				/>
			)}
			translations={{
				title: t("Invitation list"),
				empty: t("No data available."),
			}}
			initialSearch={{
				search: "",
				searchFields: [{ name: "contact.name", label: t("Name") }],
			}}
		/>
	);
};
